/**
 * AICoach — P10 Claude-powered 5-insight panel.
 */
import { useState, useCallback } from "react";
import { Sparkles, Lightbulb, AlertCircle, ShieldAlert, Heart } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";

const severityMeta = {
  praise: { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", icon: Heart },
  neutral: { color: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200", icon: Lightbulb },
  caution: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", icon: AlertCircle },
  warning: { color: "text-rose-700", bg: "bg-rose-50", border: "border-rose-200", icon: ShieldAlert },
};

export default function AICoach({ request, result }) {
  const [insights, setInsights] = useState(null);
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = useCallback(async () => {
    if (!request) return;
    setBusy(true);
    try {
      const { data } = await api.post("/snowball/ai-coach", { request, result });
      setInsights(data.insights || []);
      setSource(data.source || "");
      if (data.note) toast.info(data.note);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI coach failed");
    } finally {
      setBusy(false);
    }
  }, [request, result]);

  return (
    <div className="card-flat p-5" data-testid="snowball-ai-coach">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#C85A32]" />
          <h4 className="font-serif text-lg">AI Coach</h4>
          {source && (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {source === "template" ? "Template" : "Claude Sonnet 4.5"}
            </span>
          )}
        </div>
        <Button
          onClick={generate}
          disabled={busy}
          variant="outline"
          data-testid="snowball-ai-coach-generate"
        >
          {busy ? "Analysing…" : insights ? "Regenerate insights" : "Generate 5 insights"}
        </Button>
      </div>

      {!insights && !busy && (
        <p className="text-sm text-muted-foreground">
          Get 5 personalised insights about your snowball plan — including the highest-leverage
          variable, risk gaps, and one concrete next step.
        </p>
      )}

      {busy && (
        <div className="text-sm text-muted-foreground animate-pulse">
          Asking the coach for personalised insights…
        </div>
      )}

      {insights && insights.length > 0 && (
        <div className="space-y-3 mt-2" data-testid="ai-coach-insights">
          {insights.map((it, i) => {
            const meta = severityMeta[it.severity] || severityMeta.neutral;
            const Icon = meta.icon;
            return (
              <div
                key={`${it.title}-${i}`}
                className={`rounded-md border ${meta.border} ${meta.bg} p-4`}
                data-testid={`ai-insight-${i}`}
              >
                <div className="flex items-start gap-3">
                  <Icon size={16} className={`${meta.color} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1">
                    <h5 className={`font-medium ${meta.color}`}>{it.title}</h5>
                    <p className="text-sm text-foreground mt-1">{it.body}</p>
                    {it.recommendation && (
                      <div className="mt-2 text-xs text-foreground border-l-2 border-current pl-3 opacity-80">
                        <strong>Action:</strong> {it.recommendation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
